<template>
  <WdsSelect
    v-model="selectedBatchObjects"
    multi-select
    searchable
    :list="batches"
    :placeholder="$t('Select batch')"
    max-labels-visible="1"
    :max-width="240"
    class="tw-w-[240px]"
    can-deselect
    labels-primary
    :data-test="dataTest || 'batch-filter-multi-select'"
  >
    <template #option="{ option }">
      <WdsStack align-center justify-space-between gap-sm class="tw-w-full tw-mr-1">
        <div>{{ option.label }}</div>
        <WdsText tertiary>{{ option.count }}</WdsText>
      </WdsStack>
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import { computed } from 'vue'
import { $t } from '@/composables/translation'

interface BatchOption {
  id: string
  name: string
  processingItemsCount?: number
  processedItemsCount?: number
}

const props = defineProps<{
  batches: BatchOption[]
  selectedBatchIds: string[]
  dataTest?: string
}>()

const emit = defineEmits<{
  (e: 'update:selectedBatchIds', value: string[]): void
}>()

const serverCountKey = computed(() => {
  if (props.batches.some((b) => typeof b.processingItemsCount === 'number')) {
    return 'processingItemsCount'
  }
  if (props.batches.some((b) => typeof b.processedItemsCount === 'number')) {
    return 'processedItemsCount'
  }
  return 'processingItemsCount'
})

const batches = computed(() =>
  props.batches.map((batch) => ({
    label: batch.name,
    value: batch.id,
    count: batch[serverCountKey.value] ?? 0,
  })),
)

const selectedBatchObjects = computed({
  get() {
    return props.selectedBatchIds
      .map((id) => {
        const batch = props.batches.find((b) => b.id === id)
        return batch
          ? {
              label: batch.name,
              value: batch.id,
              count: batch[serverCountKey.value] ?? 0,
            }
          : null
      })
      .filter(Boolean)
  },
  set(selectedObjects: Array<{ label: string; value: string }>) {
    const newBatchIds = [...new Set(selectedObjects.map((obj) => obj.value))]
    emit('update:selectedBatchIds', newBatchIds)
  },
})
</script>
